/**
 * Add-on sections on the fan page. An add-on with `choice: true` belongs to
 * the item's REQUIRED section: the fan picks exactly one (e.g. the kind of
 * tea), like radio buttons. The rest are optional extras, any number, like
 * checkboxes (milk, sugar). The checkout applies the same rule
 * (supabase/functions/_shared/addon-choice.ts).
 */
export interface Addon {
  name: string;
  price: number;
  choice?: boolean;
}

interface WithAddons {
  addons?: Addon[] | null;
}

export function splitAddons(item: WithAddons): { choice: Addon[]; extras: Addon[] } {
  const all = item.addons || [];
  return { choice: all.filter((a) => a.choice === true), extras: all.filter((a) => a.choice !== true) };
}

/** Tap on an add-on: a required option replaces the previous one (and stays picked); an extra toggles. */
export function toggleAddon(item: WithAddons, selected: Addon[], addon: Addon): Addon[] {
  const isPicked = selected.some((a) => a.name === addon.name);
  if (addon.choice === true) {
    if (isPicked) return selected;
    const choiceNames = new Set(splitAddons(item).choice.map((a) => a.name));
    return [...selected.filter((a) => !choiceNames.has(a.name)), addon];
  }
  return isPicked ? selected.filter((a) => a.name !== addon.name) : [...selected, addon];
}

/** A product with a required section needs exactly one of its options; extras never block. */
export function canAddWithAddons(item: WithAddons, selected: Addon[]): boolean {
  const choiceNames = new Set(splitAddons(item).choice.map((a) => a.name));
  if (choiceNames.size === 0) return true;
  return selected.filter((a) => choiceNames.has(a.name)).length === 1;
}

/** The picked add-ons in the menu's order (the required choice first), so staff read the tea before the milk. */
export function orderSelection(item: WithAddons, selected: Addon[]): Addon[] {
  const order = (item.addons || []).map((a) => a.name);
  return [...selected].sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
}
